import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserRound } from "lucide-react";

/**
 * v72 — Per-child invite dialog. Primary parent only.
 *
 * Extracted from Children.jsx (v76 refactor) — pure presentational
 * component. All state lives in the parent; this just renders.
 */
export function InviteCoparentDialog({
  open,
  onOpenChange,
  viewing,
  kids,
  inviteChildIds,
  inviteEmail,
  setInviteEmail,
  inviteResult,
  inviteSending,
  submitInvite,
  copyInviteLink,
  toggleInviteChild,
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="bg-white max-w-md" data-testid="child-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
            <UserRound className="w-5 h-5" /> Invite co-parent
          </DialogTitle>
          {viewing && (
            <p className="text-xs text-[#6F828A] mt-1">
              Generates a single-use invite link. The co-parent will only see information for the children you select below.
            </p>
          )}
        </DialogHeader>

        {!inviteResult && viewing && (
          <form onSubmit={submitInvite} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">Children included</Label>
              <ul className="mt-2 space-y-1.5">
                {kids.filter((c) => !c.archived_at).map((c) => {
                  const isPrimaryChild = c.id === viewing.id;
                  const checked = inviteChildIds.includes(c.id);
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => toggleInviteChild(c.id)}
                        disabled={isPrimaryChild}
                        className={`w-full flex items-center gap-2 text-sm border rounded-sm p-2 text-left transition-colors ${checked ? "border-[#1F9FA4] bg-[#E0F2F2]/40" : "border-[#D9E1E3] hover:border-[#A7D8DA]"} ${isPrimaryChild ? "opacity-90 cursor-default" : "cursor-pointer"}`}
                        data-testid={`invite-child-toggle-${c.id}`}
                      >
                        <span className={`w-4 h-4 rounded-sm border flex-shrink-0 ${checked ? "bg-[#1F9FA4] border-[#1F9FA4]" : "border-[#A7C5DA]"}`}>
                          {checked && <span className="block text-white text-[10px] leading-none text-center mt-[1px]">✓</span>}
                        </span>
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                        <span className="font-medium">{c.name}</span>
                        {isPrimaryChild && <span className="text-[10px] text-[#6F828A] ml-auto uppercase tracking-[0.08em]">always included</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div>
              <Label htmlFor="invite-email">Email <span className="text-[#6F828A] font-normal">(optional)</span></Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="They'll receive a link to join"
                data-testid="invite-email-input"
              />
              <p className="text-[11px] text-[#6F828A] mt-1">
                Leave blank to copy the link manually instead.
              </p>
            </div>

            <DialogFooter className="flex-wrap gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="child-invite-cancel">Cancel</Button>
              <Button
                type="submit"
                disabled={inviteSending || inviteChildIds.length === 0}
                className="bg-[#1F9FA4] hover:bg-[#178085]"
                data-testid="child-invite-submit"
              >
                {inviteSending ? "Generating…" : "Generate invite"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {inviteResult && (
          <div className="space-y-3" data-testid="child-invite-result">
            <div className="border border-[#A7D8DA] bg-[#E0F2F2] rounded-sm p-3 text-sm text-[#0F5658]">
              {inviteResult.emailed ? (
                <span>Email sent. They&apos;ll get a join link in their inbox.</span>
              ) : (
                <span>Invite link is ready. Share it directly with the new co-parent.</span>
              )}
            </div>
            {inviteResult.join_url && (
              <div>
                <Label className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">Invite link</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    readOnly
                    value={inviteResult.join_url}
                    onFocus={(e) => e.target.select()}
                    className="font-mono-data text-xs"
                    data-testid="invite-join-url"
                  />
                  <Button
                    type="button"
                    onClick={copyInviteLink}
                    className="bg-[#1F9FA4] hover:bg-[#178085]"
                    data-testid="invite-copy-link"
                  >
                    Copy
                  </Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)} data-testid="child-invite-done">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
